import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CreateInvestorModal from '../components/CreateInvestorModal';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { adminAPI, getApiUrl } from '../lib/api';

const InvestorsManagement = ({ readOnly = false }) => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateInvestorModal, setShowCreateInvestorModal] = useState(false);
    const [createInvestorType, setCreateInvestorType] = useState('INDIVIDUAL');
    const [editingItem, setEditingItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'info' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [investorsRes, networksRes] = await Promise.all([
                fetch(getApiUrl('admin/users/investors'), { headers }),
                fetch(getApiUrl('admin/users/investor-networks'), { headers })
            ]);

            const investorsData = await investorsRes.json();
            const networksData = await networksRes.json();

            // Tag data
            const taggedInvestors = investorsData.map(i => ({ ...i, type: 'INDIVIDUAL' }));
            const taggedNetworks = networksData.map(n => ({ ...n, type: 'NETWORK' }));

            setAllItems([...taggedNetworks, ...taggedInvestors]);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Paginate data
    const totalPages = Math.ceil(allItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = allItems.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setCreateInvestorType(item.type);
        setShowCreateInvestorModal(true);
    };

    const handleCreate = () => {
        setEditingItem(null);
        setCreateInvestorType('INDIVIDUAL');
        setShowCreateInvestorModal(true);
    };

    const canResetPassword = (item) => item.role !== 'ADMIN' && item.role !== 'VIEWER';

    const handleResetPassword = (item) => {
        setConfirm({
            isOpen: true,
            title: 'Reset password',
            message: `Generate a temporary password for "${item.organizationName || item.name}" (${item.email})?`,
            type: 'info',
            onConfirm: async () => {
                setConfirm((c) => ({ ...c, isOpen: false }));
                try {
                    const data = await adminAPI.resetUserPassword(item.id);
                    const tempPassword = data.tempPassword;
                    if (!tempPassword) {
                        throw new Error('Server did not return a temporary password.');
                    }
                    const copyToClipboard = async () => {
                        try {
                            await navigator.clipboard.writeText(tempPassword);
                        } catch (err) {
                            console.error(err);
                        }
                    };
                    setAlert({
                        isOpen: true,
                        title: 'Password reset',
                        message: (
                            <div className="space-y-3 text-left">
                                <p className="text-sm text-gray-600">Share this temporary password securely with the user.</p>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-2">
                                    <code className="text-sm font-mono font-semibold text-gray-900 break-all select-all">{tempPassword}</code>
                                    <button type="button" onClick={copyToClipboard} className="shrink-0 px-2 py-1 text-xs bg-blue-600 text-white rounded">Copy</button>
                                </div>
                            </div>
                        ),
                        type: 'success'
                    });
                } catch (error) {
                    setAlert({
                        isOpen: true,
                        title: 'Error',
                        message: error.message || 'Failed to reset password',
                        type: 'error'
                    });
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 flex flex-col h-[calc(100vh-80px)]">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Investors & Networks</h2>

                {/* Single Add Button - Only show if not read-only */}
                {!readOnly && (
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Add Investor</span>
                    </button>
                )}
            </div>

            {/* Unified List - Scrollable */}
            <div className="flex-1 bg-white rounded-lg shadow overflow-hidden mb-6">
                <div className="overflow-x-auto h-full">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Organization
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentItems.map((item) => {
                                const isNetwork = item.type === 'NETWORK';
                                return (
                                    <tr
                                        key={`${item.type}-${item.id}`}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => {
                                            if (isNetwork) {
                                                navigate(`/${tenantId}/investors/networks/${item.id}`);
                                            } else {
                                                navigate(`/${tenantId}/investors/${item.id}`);
                                            }
                                        }}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className={`h-10 w-10 rounded-full ${isNetwork ? 'bg-gradient-to-r from-purple-500 to-pink-600' : 'bg-gradient-to-r from-yellow-500 to-yellow-600'} flex items-center justify-center`}>
                                                        <span className="text-white font-medium">
                                                            {(item.organizationName || item.name)?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {item.organizationName || item.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {isNetwork ? (item.organizationType || 'Network') : (item.organizationName || 'Individual')}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isNetwork ? 'bg-purple-100 text-purple-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {isNetwork ? 'Network' : 'Individual'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{item.email}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{item.department || item.organizationName || 'N/A'}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                Active
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-3 justify-end" onClick={(e) => e.stopPropagation()}>
                                                {canResetPassword(item) && !readOnly && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleResetPassword(item)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Reset password"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isNetwork) {
                                                            navigate(`/${tenantId}/investors/networks/${item.id}`);
                                                        } else {
                                                            navigate(`/${tenantId}/investors/${item.id}`);
                                                        }
                                                    }}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* Empty State */}
                            {allItems.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No investors yet</h3>
                                        <p className="mt-1 text-sm text-gray-500">Get started by creating a new investor or network.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Fixed Bottom Section - Stats and Pagination */}
            <div className="bg-white border-t border-gray-200 py-2 px-4 rounded-lg shadow">
                <div className="flex items-center justify-between">
                    {/* Stats in Single Row - Compact */}
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1.5">
                            <div className="w-7 h-7 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-3.5 h-3.5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 leading-none">Investors</p>
                                <p className="text-base font-bold text-gray-900 leading-tight">{allItems.filter(i => i.type === 'INDIVIDUAL').length}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1.5">
                            <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 leading-none">Networks</p>
                                <p className="text-base font-bold text-gray-900 leading-tight">{allItems.filter(i => i.type === 'NETWORK').length}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1.5">
                            <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 leading-none">Total</p>
                                <p className="text-base font-bold text-gray-900 leading-tight">{allItems.length}</p>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <div className="flex items-center space-x-1">
                                {[...Array(totalPages)].map((_, index) => {
                                    const page = index + 1;
                                    // Show first page, last page, current page, and pages around current
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => goToPage(page)}
                                                className={`px-3 py-1 rounded-md text-sm font-medium ${currentPage === page
                                                    ? 'bg-blue-600 text-white'
                                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                                        return <span key={page} className="px-2 text-gray-500">...</span>;
                                    }
                                    return null;
                                })}
                            </div>
                            <button
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Investor/Network Modal - Only show if not read-only */}
            {!readOnly && (
                <CreateInvestorModal
                    isOpen={showCreateInvestorModal}
                    onClose={() => {
                        setShowCreateInvestorModal(false);
                        setEditingItem(null);
                    }}
                    onSuccess={loadData}
                    initialType={createInvestorType}
                    initialData={editingItem}
                    isEdit={!!editingItem}
                />
            )}

            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm((c) => ({ ...c, isOpen: false }))}
                onConfirm={confirm.onConfirm}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
        </div >
    );
};

export default InvestorsManagement;
